import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAdmin } from '../context/AdminContext';
import { 
  LayoutDashboard, Calendar, Star, MessageSquare, LogOut, 
  DollarSign, Clock, CheckCircle, XCircle, Trash2, Eye, Users, Download,
  Home as HomeIcon, CalendarRange, Image, Plus, Bell, X, Check,
  Percent, Edit, Save, Settings, Phone, Mail, MapPin, CreditCard, HelpCircle
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, 
  AlertDialogTitle, AlertDialogTrigger 
} from '../components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import { toast, Toaster } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Notification Bell Component
const NotificationBell = ({ getAuthHeaders, onNavigateToTab }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications`, getAuthHeaders());
      setNotifications(res.data || []);
      setUnreadCount((res.data || []).filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications/unread-count`, getAuthHeaders());
      setUnreadCount(res.data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/admin/notifications/${notificationId}/read`, {}, getAuthHeaders());
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Error al marcar como leída');
    }
  };

  const markAllAsRead = async () => {
    setLoading(true);
    try {
      await axios.patch(`${API}/admin/notifications/read-all`, {}, getAuthHeaders());
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('Todas marcadas como leídas');
    } catch (error) {
      toast.error('Error al marcar como leídas');
    } finally {
      setLoading(false);
    }
  };

  const deleteAllNotifications = async () => {
    if (notifications.length === 0) return;
    setLoading(true);
    try {
      await Promise.all(notifications.map(n => 
        axios.delete(`${API}/admin/notifications/${n.id}`, getAuthHeaders())
      ));
      setNotifications([]);
      setUnreadCount(0);
      toast.success('Todas las notificaciones eliminadas');
    } catch (error) {
      toast.error('Error al eliminar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const deleteNotification = async (notificationId, isRead) => {
    try {
      await axios.delete(`${API}/admin/notifications/${notificationId}`, getAuthHeaders());
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (!isRead) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleNavigateToMessages = () => {
    setIsOpen(false);
    if (onNavigateToTab) {
      onNavigateToTab('messages');
    }
  };

  const formatTimeAgo = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: es });
    } catch {
      return '';
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(price);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => { setIsOpen(!isOpen); if (!isOpen) fetchNotifications(); }}
        className="p-2 text-white/80 hover:text-white relative"
        title="Notificaciones"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="fixed sm:absolute inset-x-2 sm:inset-x-auto sm:right-0 top-14 sm:top-auto sm:mt-2 w-auto sm:w-96 bg-white rounded-sm shadow-xl border border-[#E5E0D6] z-50 max-h-[70vh] overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-[#E5E0D6] bg-[#F5F2EB]">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#1A1A1A]">Notificaciones</h3>
              <button onClick={() => setIsOpen(false)} className="sm:hidden p-1 text-[#5A5A5A]">
                <X className="w-4 h-4" />
              </button>
            </div>
            {notifications.length > 0 && (
              <div className="flex items-center gap-3 mt-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    disabled={loading}
                    className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                  >
                    Marcar todas como leídas
                  </button>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="text-xs text-red-600 hover:underline ml-auto">
                      Borrar todas
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Eliminar todas las notificaciones?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Se eliminarán {notifications.length} notificaciones. Esta acción no se puede deshacer.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={deleteAllNotifications} disabled={loading}>
                        {loading ? 'Eliminando...' : 'Eliminar todas'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto max-h-[50vh]">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 text-[#C6A87C] mx-auto mb-2 opacity-50" />
                <p className="text-[#5A5A5A] text-sm">No hay notificaciones</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-4 py-3 border-b border-[#E5E0D6] hover:bg-[#F5F2EB]/50 transition-colors ${
                    !notification.is_read ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Indicator */}
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                      !notification.is_read ? 'bg-blue-500' : 'bg-transparent'
                    }`} />
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-[#1A1A1A] text-sm">{notification.title}</p>
                        <span className="text-[10px] text-[#5A5A5A] whitespace-nowrap">
                          {formatTimeAgo(notification.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-[#5A5A5A] mt-0.5">{notification.message}</p>
                      
                      {/* Booking details */}
                      {notification.type === 'new_booking' && notification.data && (
                        <div className="mt-2 p-2 bg-[#F5F2EB] rounded text-xs space-y-1">
                          <div className="flex justify-between">
                            <span className="text-[#5A5A5A]">Cabañas:</span>
                            <span className="font-medium">{notification.data.cabin_names?.join(', ')}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[#5A5A5A]">Fechas:</span>
                            <span className="font-medium">{notification.data.check_in} → {notification.data.check_out}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[#5A5A5A]">Huéspedes:</span>
                            <span className="font-medium">{notification.data.guests} personas</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[#5A5A5A]">Total:</span>
                            <span className="font-bold text-[#2F3E30]">{formatPrice(notification.data.total_price)}</span>
                          </div>
                          <div className="flex justify-between pt-1 border-t border-[#E5E0D6]">
                            <span className="text-[#5A5A5A]">Teléfono:</span>
                            <a 
                              href={`https://wa.me/${notification.data.guest_phone?.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-green-600 hover:underline"
                            >
                              {notification.data.guest_phone}
                            </a>
                          </div>
                        </div>
                      )}

                      {/* Contact message details */}
                      {notification.type === 'new_message' && notification.data && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                          <div className="flex justify-between items-center">
                            <span className="text-[#5A5A5A]">De:</span>
                            <span className="font-medium">{notification.data.sender_name}</span>
                          </div>
                          {notification.data.sender_phone && (
                            <div className="flex justify-between items-center mt-1">
                              <span className="text-[#5A5A5A]">Teléfono:</span>
                              <a 
                                href={`https://wa.me/${notification.data.sender_phone?.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-green-600 hover:underline"
                              >
                                {notification.data.sender_phone}
                              </a>
                            </div>
                          )}
                          <p className="text-[#5A5A5A] mt-2 text-center">
                            <button 
                              onClick={handleNavigateToMessages}
                              className="text-blue-600 hover:underline"
                            >
                              Ver mensaje completo →
                            </button>
                          </p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-2">
                        {!notification.is_read && (
                          <button
                            onClick={() => markAsRead(notification.id)}
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <Check className="w-3 h-3" /> Marcar como leída
                          </button>
                        )}
                        <button
                          onClick={() => deleteNotification(notification.id, notification.is_read)}
                          className="text-xs text-red-500 hover:underline flex items-center gap-1 ml-auto"
                        >
                          <Trash2 className="w-3 h-3" /> Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await onLogin(username, password);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F5F2EB] flex items-center justify-center p-4">
      <div className="bg-white p-6 md:p-8 rounded-sm card-shadow max-w-md w-full">
        <div className="text-center mb-6 md:mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A]">Cabañas RIBBA</h1>
          <p className="text-[#5A5A5A] mt-2 text-sm md:text-base">Panel de Administración</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#5A5A5A] mb-2">Usuario</label>
            <input
              type="text"
              data-testid="admin-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border border-[#E5E0D6] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#C6A87C]"
              placeholder="Ingresa tu usuario"
              autoComplete="username"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5A5A5A] mb-2">Contraseña</label>
            <input
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-[#E5E0D6] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#C6A87C]"
              placeholder="Ingresa tu contraseña"
              autoComplete="current-password"
              required
            />
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button type="submit" data-testid="admin-login-btn" disabled={loading} className="w-full btn-primary disabled:opacity-50">
            {loading ? 'Ingresando...' : 'Ingresar'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Dashboard Component
const AdminDashboard = () => {
  const { logout, getAuthHeaders } = useAdmin();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [cabins, setCabins] = useState([]);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Check if current user has admin (full) access
  const isFullAdmin = currentUser?.role === 'admin';

  useEffect(() => { fetchData(); fetchCurrentUser(); }, []);

  const fetchCurrentUser = async () => {
    try {
      const res = await axios.get(`${API}/admin/users/me`, getAuthHeaders());
      setCurrentUser(res.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, bookingsRes, reviewsRes, messagesRes, seasonsRes, cabinsRes, businessRes, faqsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, getAuthHeaders()),
        axios.get(`${API}/admin/bookings`, getAuthHeaders()),
        axios.get(`${API}/admin/reviews`, getAuthHeaders()),
        axios.get(`${API}/admin/messages`, getAuthHeaders()),
        axios.get(`${API}/admin/seasons`, getAuthHeaders()),
        axios.get(`${API}/admin/cabins`, getAuthHeaders()),
        axios.get(`${API}/admin/business-info`, getAuthHeaders()),
        axios.get(`${API}/admin/faqs`, getAuthHeaders())
      ]);
      
      setStats(statsRes.data);
      setBookings(bookingsRes.data || []);
      setReviews(reviewsRes.data || []);
      setMessages(messagesRes.data || []);
      setSeasons(seasonsRes.data || []);
      setCabins(cabinsRes.data || []);
      setBusinessInfo(businessRes.data);
      setFaqs(faqsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.response?.data?.detail || 'Error al cargar datos. Tu sesión puede haber expirado.');
      if (error.response?.status === 401) {
        toast.error('Sesión expirada. Por favor inicia sesión de nuevo.');
        logout();
      } else {
        toast.error('Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  };

  // ... rest of functions remain the same

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}?status=${status}`, {}, getAuthHeaders());
      toast.success('Reservación actualizada');
      fetchData();
    } catch (error) { toast.error('Error al actualizar'); }
  };

  const deleteBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, getAuthHeaders());
      toast.success('Reservación eliminada');
      fetchData();
    } catch (error) { toast.error('Error al eliminar'); }
  };

  const toggleReviewApproval = async (reviewId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/reviews/${reviewId}?is_approved=${!currentStatus}`, {}, getAuthHeaders());
      toast.success('Reseña actualizada');
      fetchData();
    } catch (error) { toast.error('Error al actualizar'); }
  };

  const deleteReview = async (reviewId) => {
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, getAuthHeaders());
      toast.success('Reseña eliminada');
      fetchData();
    } catch (error) { toast.error('Error al eliminar'); }
  };

  const deleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/admin/messages/${messageId}`, getAuthHeaders());
      toast.success('Mensaje eliminado');
      fetchData();
    } catch (error) { toast.error('Error al eliminar'); }
  };

  const toggleSeason = async (seasonId) => {
    try {
      await axios.patch(`${API}/admin/seasons/${seasonId}/toggle`, {}, getAuthHeaders());
      toast.success('Temporada actualizada');
      fetchData();
    } catch (error) { toast.error('Error al actualizar'); }
  };

  const deleteSeason = async (seasonId) => {
    try {
      await axios.delete(`${API}/admin/seasons/${seasonId}`, getAuthHeaders());
      toast.success('Temporada eliminada');
      fetchData();
    } catch (error) { toast.error('Error al eliminar'); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return format(new Date(dateStr), "d MMM yyyy", { locale: es });
  };

  const getStatusBadge = (status) => {
    const styles = { pending: 'bg-yellow-100 text-yellow-800', paid: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800' };
    const labels = { pending: 'Pendiente', paid: 'Pagado', cancelled: 'Cancelado' };
    return <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status]}`}>{labels[status]}</span>;
  };

  if (loading) {
    return <div className="min-h-screen bg-[#F5F2EB] flex items-center justify-center"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F5F2EB] overflow-y-auto">
      <Toaster position="top-center" richColors />
      
      {/* Mobile-friendly Header */}
      <header className="bg-[#2F3E30] text-white py-3 md:py-4 sticky top-0 z-50">
        <div className="container-ribba flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            <h1 className="font-heading text-lg md:text-xl font-bold">Admin</h1>
            <span className="hidden sm:inline text-white/60 text-sm">Cabañas RIBBA</span>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            {/* Current user indicator */}
            {currentUser && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-white/10 rounded-sm">
                <span className="text-sm text-white/90">{currentUser.name}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${currentUser.role === 'admin' ? 'bg-purple-500' : 'bg-blue-500'}`}>
                  {currentUser.role === 'admin' ? 'Admin' : 'Viewer'}
                </span>
              </div>
            )}
            {/* Notification Bell */}
            <NotificationBell getAuthHeaders={getAuthHeaders} onNavigateToTab={setActiveTab} />
            <button onClick={fetchData} className="p-2 text-white/80 hover:text-white bg-white/10 rounded-sm" title="Recargar">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            </button>
            <button onClick={() => navigate('/')} className="p-2 text-white/80 hover:text-white" title="Ver sitio">
              <HomeIcon className="w-4 h-4" />
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="p-2 text-white/80 hover:text-white" title="Salir">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation - Bottom Tab Bar - raised to avoid Emergent button */}
      <nav className="md:hidden fixed bottom-12 left-0 right-0 bg-white border-t border-b border-[#E5E0D6] z-50 rounded-t-lg shadow-lg">
        <div className="flex overflow-x-auto scrollbar-hide px-2" style={{ WebkitOverflowScrolling: 'touch' }}>
          <button 
            onClick={() => setActiveTab('overview')} 
            className={`flex-shrink-0 flex flex-col items-center py-3 px-5 ${activeTab === 'overview' ? 'text-[#2F3E30] bg-[#F5F2EB] rounded-t-lg' : 'text-[#5A5A5A]'}`}
          >
            <LayoutDashboard className="w-6 h-6" />
            <span className="text-xs mt-1 whitespace-nowrap font-medium">Inicio</span>
          </button>
          <button 
            onClick={() => setActiveTab('bookings')} 
            className={`flex-shrink-0 flex flex-col items-center py-3 px-5 relative ${activeTab === 'bookings' ? 'text-[#2F3E30] bg-[#F5F2EB] rounded-t-lg' : 'text-[#5A5A5A]'}`}
          >
            <Calendar className="w-6 h-6" />
            <span className="text-xs mt-1 whitespace-nowrap font-medium">Reservas</span>
            {bookings.length > 0 && (
              <span className="absolute top-1 right-2 w-5 h-5 bg-[#C6A87C] text-white text-[10px] rounded-full flex items-center justify-center font-bold">{bookings.length}</span>
            )}
          </button>
          {/* Admin-only tabs */}
          {isFullAdmin && (
            <>
              <button 
                onClick={() => setActiveTab('pricing')} 
                className={`flex-shrink-0 flex flex-col items-center py-3 px-5 ${activeTab === 'pricing' ? 'text-[#2F3E30] bg-[#F5F2EB] rounded-t-lg' : 'text-[#5A5A5A]'}`}
              >
                <DollarSign className="w-6 h-6" />
                <span className="text-xs mt-1 whitespace-nowrap font-medium">Tarifas</span>
              </button>
              <button 
                onClick={() => setActiveTab('photos')} 
                className={`flex-shrink-0 flex flex-col items-center py-3 px-5 ${activeTab === 'photos' ? 'text-[#2F3E30] bg-[#F5F2EB] rounded-t-lg' : 'text-[#5A5A5A]'}`}
              >
                <Image className="w-6 h-6" />
                <span className="text-xs mt-1 whitespace-nowrap font-medium">Fotos</span>
              </button>
            </>
          )}
          <button 
            onClick={() => setActiveTab('reviews')} 
            className={`flex-shrink-0 flex flex-col items-center py-3 px-5 ${activeTab === 'reviews' ? 'text-[#2F3E30] bg-[#F5F2EB] rounded-t-lg' : 'text-[#5A5A5A]'}`}
          >
            <Star className="w-6 h-6" />
            <span className="text-xs mt-1 whitespace-nowrap font-medium">Reseñas</span>
          </button>
          <button 
            onClick={() => setActiveTab('messages')} 
            className={`flex-shrink-0 flex flex-col items-center py-3 px-5 relative ${activeTab === 'messages' ? 'text-[#2F3E30] bg-[#F5F2EB] rounded-t-lg' : 'text-[#5A5A5A]'}`}
          >
            <MessageSquare className="w-6 h-6" />
            <span className="text-xs mt-1 whitespace-nowrap font-medium">Mensajes</span>
            {messages.length > 0 && (
              <span className="absolute top-1 right-2 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">{messages.length}</span>
            )}
          </button>
          {/* Admin-only tabs */}
          {isFullAdmin && (
            <>
              <button 
                onClick={() => setActiveTab('faqs')} 
                className={`flex-shrink-0 flex flex-col items-center py-3 px-5 ${activeTab === 'faqs' ? 'text-[#2F3E30] bg-[#F5F2EB] rounded-t-lg' : 'text-[#5A5A5A]'}`}
              >
                <HelpCircle className="w-6 h-6" />
                <span className="text-xs mt-1 whitespace-nowrap font-medium">FAQs</span>
              </button>
              <button 
                onClick={() => setActiveTab('settings')} 
                className={`flex-shrink-0 flex flex-col items-center py-3 px-5 ${activeTab === 'settings' ? 'text-[#2F3E30] bg-[#F5F2EB] rounded-t-lg' : 'text-[#5A5A5A]'}`}
              >
                <Settings className="w-6 h-6" />
                <span className="text-xs mt-1 whitespace-nowrap font-medium">Config</span>
              </button>
              <button 
                onClick={() => setActiveTab('users')} 
                className={`flex-shrink-0 flex flex-col items-center py-3 px-5 ${activeTab === 'users' ? 'text-[#2F3E30] bg-[#F5F2EB] rounded-t-lg' : 'text-[#5A5A5A]'}`}
              >
                <Users className="w-6 h-6" />
                <span className="text-xs mt-1 whitespace-nowrap font-medium">Usuarios</span>
              </button>
            </>
          )}
        </div>
        {/* Scroll indicator */}
        <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />
      </nav>

      <main className="container-ribba py-4 md:py-8 pb-44 md:pb-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Desktop Tabs - Hidden on mobile */}
          <TabsList className="mb-6 md:mb-8 bg-white hidden md:flex flex-wrap">
            <TabsTrigger value="overview"><LayoutDashboard className="w-4 h-4 mr-2" />Resumen</TabsTrigger>
            <TabsTrigger value="bookings"><Calendar className="w-4 h-4 mr-2" />Reservas ({bookings.length})</TabsTrigger>
            {isFullAdmin && <TabsTrigger value="pricing"><DollarSign className="w-4 h-4 mr-2" />Tarifas</TabsTrigger>}
            {isFullAdmin && <TabsTrigger value="photos"><Image className="w-4 h-4 mr-2" />Fotos</TabsTrigger>}
            <TabsTrigger value="reviews"><Star className="w-4 h-4 mr-2" />Reseñas ({reviews.length})</TabsTrigger>
            <TabsTrigger value="messages"><MessageSquare className="w-4 h-4 mr-2" />Mensajes ({messages.length})</TabsTrigger>
            {isFullAdmin && <TabsTrigger value="faqs"><HelpCircle className="w-4 h-4 mr-2" />FAQs ({faqs.length})</TabsTrigger>}
            {isFullAdmin && <TabsTrigger value="settings"><Settings className="w-4 h-4 mr-2" />Configuración</TabsTrigger>}
            {isFullAdmin && <TabsTrigger value="users"><Users className="w-4 h-4 mr-2" />Usuarios</TabsTrigger>}
          </TabsList>

          {/* Mobile Section Title */}
          <div className="md:hidden mb-4">
            <h2 className="font-heading text-xl font-semibold text-[#1A1A1A]">
              {activeTab === 'overview' && 'Resumen'}
              {activeTab === 'bookings' && 'Reservaciones'}
              {activeTab === 'pricing' && 'Tarifas'}
              {activeTab === 'photos' && 'Fotos'}
              {activeTab === 'reviews' && 'Reseñas'}
              {activeTab === 'messages' && 'Mensajes'}
              {activeTab === 'faqs' && 'Preguntas Frecuentes'}
              {activeTab === 'settings' && 'Configuración'}
              {activeTab === 'users' && 'Usuarios'}
            </h2>
          </div>

          <TabsContent value="overview">
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
              <StatCard icon={Calendar} label="Reservaciones" value={stats?.bookings?.total || 0} color="blue" />
              <StatCard icon={Clock} label="Pendientes" value={stats?.bookings?.pending || 0} color="yellow" />
              <StatCard icon={DollarSign} label="Ingresos" value={`$${(stats?.revenue || 0).toLocaleString()}`} color="green" />
              <StatCard icon={Star} label="Reseñas" value={stats?.reviews || 0} color="purple" />
            </div>
            <div className="bg-white rounded-sm card-shadow p-4 md:p-6">
              <h3 className="font-heading text-lg md:text-xl font-semibold mb-4">Reservaciones Recientes</h3>
              {bookings.length > 0 ? (
                <div className="space-y-3 md:space-y-4">
                  {bookings.slice(0, 5).map((booking) => (
                    <div key={booking.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 md:p-4 bg-[#F5F2EB] rounded-sm gap-2">
                      <div>
                        <p className="font-medium text-[#1A1A1A] text-sm md:text-base">{booking.guest_name}</p>
                        <p className="text-xs md:text-sm text-[#5A5A5A]">{booking.cabin_name} • {formatDate(booking.check_in)}</p>
                      </div>
                      <div className="flex items-center gap-2 md:gap-3">
                        <span className="font-medium text-sm md:text-base">${booking.total_price.toLocaleString()}</span>
                        {getStatusBadge(booking.payment_status)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-[#5A5A5A] text-center py-8">No hay reservaciones</p>}
            </div>
          </TabsContent>

          <TabsContent value="bookings">
            <BookingsTable bookings={bookings} formatDate={formatDate} getStatusBadge={getStatusBadge} updateBookingStatus={updateBookingStatus} deleteBooking={deleteBooking} />
          </TabsContent>

          {isFullAdmin && (
            <TabsContent value="pricing">
              <PricingManager cabins={cabins} seasons={seasons} toggleSeason={toggleSeason} deleteSeason={deleteSeason} fetchData={fetchData} getAuthHeaders={getAuthHeaders} />
            </TabsContent>
          )}

          {isFullAdmin && (
            <TabsContent value="photos">
              <PhotosManager cabins={cabins} fetchData={fetchData} getAuthHeaders={getAuthHeaders} />
            </TabsContent>
          )}

          <TabsContent value="reviews">
            <ReviewsManager reviews={reviews} formatDate={formatDate} toggleReviewApproval={toggleReviewApproval} deleteReview={deleteReview} />
          </TabsContent>

          <TabsContent value="messages">
            <MessagesManager messages={messages} formatDate={formatDate} deleteMessage={deleteMessage} getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          {isFullAdmin && (
            <TabsContent value="faqs">
              <FAQsManager faqs={faqs} fetchData={fetchData} getAuthHeaders={getAuthHeaders} />
            </TabsContent>
          )}

          {isFullAdmin && (
            <TabsContent value="settings">
              <SettingsManager businessInfo={businessInfo} fetchData={fetchData} getAuthHeaders={getAuthHeaders} />
            </TabsContent>
          )}

          {isFullAdmin && (
            <TabsContent value="users">
              <UsersManager getAuthHeaders={getAuthHeaders} />
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
};

// Stat Card
const StatCard = ({ icon: Icon, label, value, color }) => {
  const colors = { blue: 'bg-blue-100 text-blue-600', yellow: 'bg-yellow-100 text-yellow-600', green: 'bg-green-100 text-green-600', purple: 'bg-purple-100 text-purple-600' };
  return (
    <div className="bg-white p-3 md:p-6 rounded-sm card-shadow">
      <div className="flex items-center gap-2 md:gap-4">
        <div className={`w-8 h-8 md:w-12 md:h-12 rounded-full flex items-center justify-center ${colors[color]}`}><Icon className="w-4 h-4 md:w-6 md:h-6" /></div>
        <div className="min-w-0 flex-1">
          <p className="text-[#5A5A5A] text-[10px] md:text-sm truncate">{label}</p>
          <p className="text-lg md:text-2xl font-heading font-bold text-[#1A1A1A] truncate">{value}</p>
        </div>
      </div>
    </div>
  );
};

// Bookings Table
const BookingsTable = ({ bookings, formatDate, getStatusBadge, updateBookingStatus, deleteBooking }) => {
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  // Filter for display (null = show all)
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  // Month selector for export
  const [exportMonth, setExportMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const toggleExpand = (bookingId) => {
    setExpandedBooking(expandedBooking === bookingId ? null : bookingId);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const displayed = getDisplayedBookings();
    if (selectedIds.length === displayed.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(displayed.map(b => b.id));
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.length === 0) return;
    setDeleting(true);
    try {
      await Promise.all(selectedIds.map(id => deleteBooking(id)));
      setSelectedIds([]);
      toast.success(`${selectedIds.length} reservación(es) eliminada(s)`);
    } catch (error) {
      toast.error('Error al eliminar reservaciones');
    } finally {
      setDeleting(false);
    }
  };

  // Parse guest distribution from notes
  const parseGuestDistribution = (notes) => {
    if (!notes) return null;
    const match = notes.match(/Distribución de huéspedes:\s*(.+?)(?:\n|$)/i) || 
                  notes.match(/Guest distribution:\s*(.+?)(?:\n|$)/i);
    return match ? match[1] : null;
  };

  // Get months for export dropdown (last 12 months + next 12 months)
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = -12; i <= 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
      options.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return options;
  };

  // Filter bookings for display (by date range)
  const getDisplayedBookings = () => {
    let filtered = [...bookings];
    if (filterStartDate) {
      filtered = filtered.filter(b => b.check_in >= filterStartDate);
    }
    if (filterEndDate) {
      filtered = filtered.filter(b => b.check_in <= filterEndDate);
    }
    return filtered.sort((a, b) => new Date(b.check_in) - new Date(a.check_in));
  };

  // Filter bookings for export (by month)
  const getExportBookings = () => {
    const [year, month] = exportMonth.split('-');
    return bookings.filter(booking => {
      const checkIn = new Date(booking.check_in);
      return checkIn.getFullYear() === parseInt(year) && checkIn.getMonth() + 1 === parseInt(month);
    });
  };

  // Export to CSV
  const exportToCSV = () => {
    const exportBookings = getExportBookings();
    if (exportBookings.length === 0) {
      toast.error('No hay reservaciones para exportar en el mes seleccionado');
      return;
    }

    const headers = ['Nombre', 'Celular', 'Email', 'Cabañas', 'Personas', 'Check-in', 'Check-out', 'Noches', 'Monto', 'Estado'];
    const rows = exportBookings.map(booking => {
      const cabins = booking.cabin_names?.join(', ') || booking.cabin_name || '';
      return [
        booking.guest_name,
        booking.guest_phone,
        booking.guest_email || '',
        cabins,
        booking.guests,
        booking.check_in,
        booking.check_out,
        booking.total_nights,
        `$${booking.total_price.toLocaleString()}`,
        booking.payment_status === 'paid' ? 'Pagado' : booking.payment_status === 'cancelled' ? 'Cancelado' : 'Pendiente'
      ];
    });

    const totalAmount = exportBookings.reduce((sum, b) => sum + b.total_price, 0);
    const totalGuests = exportBookings.reduce((sum, b) => sum + b.guests, 0);
    rows.push(['', '', '', '', '', '', '', '', '', '']);
    rows.push(['TOTALES', '', '', `${exportBookings.length} reservaciones`, `${totalGuests} personas`, '', '', '', `$${totalAmount.toLocaleString()}`, '']);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    const [year, month] = exportMonth.split('-');
    const monthName = new Date(year, month - 1).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
    link.setAttribute('download', `Reservaciones_${monthName.replace(' ', '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Reporte exportado');
  };

  const clearFilters = () => {
    setFilterStartDate('');
    setFilterEndDate('');
  };

  const displayedBookings = getDisplayedBookings();
  const hasFilters = filterStartDate || filterEndDate;

  return (
    <div className="space-y-4">
      {/* Export Controls */}
      <div className="bg-white rounded-sm card-shadow p-4">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-[#1A1A1A]">Exportar Reporte Mensual</h3>
            <p className="text-sm text-[#5A5A5A]">Descarga un CSV con las reservaciones del mes seleccionado</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={exportMonth}
              onChange={(e) => setExportMonth(e.target.value)}
              className="px-3 py-2 border border-[#E5E0D6] rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#C6A87C]"
            >
              {getMonthOptions().map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button onClick={exportToCSV} className="btn-primary flex items-center gap-2 text-sm">
              <Download className="w-4 h-4" />
              Exportar CSV
            </button>
          </div>
        </div>
      </div>

      {/* Filter and Selection Controls */}
      <div className="bg-white rounded-sm card-shadow p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <h3 className="font-semibold text-[#1A1A1A] mb-2">Filtrar Lista</h3>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm text-[#5A5A5A]">Desde:</label>
                <input
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="px-2 py-1.5 border border-[#E5E0D6] rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#C6A87C]"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-[#5A5A5A]">Hasta:</label>
                <input
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  className="px-2 py-1.5 border border-[#E5E0D6] rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#C6A87C]"
                />
              </div>
              {hasFilters && (
                <button onClick={clearFilters} className="text-sm text-red-600 hover:underline">
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>
          <div className="text-sm text-[#5A5A5A]">
            Mostrando: <span className="font-semibold text-[#1A1A1A]">{displayedBookings.length}</span> de {bookings.length}
          </div>
        </div>

        {/* Multi-select controls */}
        {displayedBookings.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#E5E0D6] flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox" 
                checked={selectedIds.length === displayedBookings.length && displayedBookings.length > 0}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-gray-300"
              />
              <span className="text-sm text-[#5A5A5A]">Seleccionar todos ({displayedBookings.length})</span>
            </label>
            {selectedIds.length > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button className="ml-auto px-3 py-1.5 bg-red-500 text-white text-sm rounded-sm hover:bg-red-600 flex items-center gap-2">
                    <Trash2 className="w-4 h-4" />
                    Eliminar ({selectedIds.length})
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Eliminar {selectedIds.length} reservación(es)?</AlertDialogTitle>
                    <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={deleteSelected} disabled={deleting}>
                      {deleting ? 'Eliminando...' : 'Eliminar'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </div>

      {/* Bookings Table - Desktop */}
      <div className="bg-white rounded-sm card-shadow overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F5F2EB]">
              <tr>
                <th className="px-3 py-3 text-left"><span className="sr-only">Seleccionar</span></th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[#5A5A5A]">Huésped</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[#5A5A5A]">Cabañas</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[#5A5A5A]">Fechas</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[#5A5A5A]">Huéspedes</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[#5A5A5A]">Total</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[#5A5A5A]">Estado</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[#5A5A5A]">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E0D6]">
              {displayedBookings.map((booking) => {
                const isExpanded = expandedBooking === booking.id;
                const guestDistribution = parseGuestDistribution(booking.notes);
                const cabinNames = booking.cabin_names || (booking.cabin_name ? [booking.cabin_name] : []);
                const cabinCount = cabinNames.length || 1;
                const isSelected = selectedIds.includes(booking.id);
                
                return (
                  <React.Fragment key={booking.id}>
                    <tr className={`hover:bg-[#F5F2EB]/50 ${isExpanded ? 'bg-[#F5F2EB]/30' : ''} ${isSelected ? 'bg-blue-50' : ''}`}>
                      <td className="px-3 py-4" onClick={(e) => e.stopPropagation()}>
                        <input 
                          type="checkbox" 
                          checked={isSelected}
                          onChange={() => toggleSelect(booking.id)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-4 cursor-pointer" onClick={() => toggleExpand(booking.id)}>
                        <p className="font-medium text-[#1A1A1A]">{booking.guest_name}</p>
                        <p className="text-xs text-[#5A5A5A]">{booking.guest_phone}</p>
                      </td>
                      <td className="px-4 py-4 cursor-pointer" onClick={() => toggleExpand(booking.id)}>
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#2F3E30] text-white text-xs font-bold">
                            {cabinCount}
                          </span>
                          <span className="text-sm">{cabinCount === 1 ? cabinNames[0] : `${cabinCount} cabañas`}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm cursor-pointer" onClick={() => toggleExpand(booking.id)}>
                        {formatDate(booking.check_in)} - {formatDate(booking.check_out)}
                        <br />
                        <span className="text-xs text-[#5A5A5A]">{booking.total_nights} noches</span>
                      </td>
                      <td className="px-4 py-4 cursor-pointer" onClick={() => toggleExpand(booking.id)}>
                        <span className="px-2 py-1 rounded bg-[#C6A87C]/20 text-[#2F3E30] text-sm font-medium">
                          {booking.guests} personas
                        </span>
                      </td>
                      <td className="px-4 py-4 font-medium text-[#2F3E30]">${booking.total_price.toLocaleString()}</td>
                      <td className="px-4 py-4">{getStatusBadge(booking.payment_status)}</td>
                      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {booking.payment_status === 'pending' && (
                            <button onClick={() => updateBookingStatus(booking.id, 'paid')} className="p-2 text-green-600 hover:bg-green-50 rounded" title="Marcar como pagado">
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          {booking.payment_status !== 'cancelled' && (
                            <button onClick={() => updateBookingStatus(booking.id, 'cancelled')} className="p-2 text-red-600 hover:bg-red-50 rounded" title="Cancelar">
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-[#F5F2EB]/50">
                        <td colSpan="8" className="px-4 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-white p-4 rounded-sm">
                              <h4 className="font-semibold text-[#1A1A1A] mb-3 flex items-center gap-2">
                                <HomeIcon className="w-4 h-4 text-[#C6A87C]" />
                                Detalle de Cabañas
                              </h4>
                              <div className="space-y-2">
                                {cabinNames.map((name, idx) => (
                                  <div key={idx} className="flex justify-between items-center p-2 bg-[#F5F2EB] rounded">
                                    <span className="font-medium">{name}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="bg-white p-4 rounded-sm">
                              <h4 className="font-semibold text-[#1A1A1A] mb-3 flex items-center gap-2">
                                <Users className="w-4 h-4 text-[#C6A87C]" />
                                Distribución de Huéspedes
                              </h4>
                              {guestDistribution ? (
                                <div className="space-y-2">
                                  {guestDistribution.split(', ').map((item, idx) => {
                                    const [cabinPart, guestsPart] = item.split(':');
                                    return (
                                      <div key={idx} className="flex justify-between items-center p-2 bg-[#F5F2EB] rounded">
                                        <span className="text-sm">{cabinPart?.trim()}</span>
                                        <span className="font-medium text-[#2F3E30]">{guestsPart?.trim()}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p className="text-sm text-[#5A5A5A]">Total: {booking.guests} personas</p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {displayedBookings.length === 0 && <p className="text-[#5A5A5A] text-center py-12">No hay reservaciones{hasFilters ? ' con estos filtros' : ''}</p>}
      </div>

      {/* Bookings Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {displayedBookings.map((booking) => {
          const isSelected = selectedIds.includes(booking.id);
          const cabinNames = booking.cabin_names || (booking.cabin_name ? [booking.cabin_name] : []);
          
          return (
            <div key={booking.id} className={`bg-white p-4 rounded-sm card-shadow ${isSelected ? 'ring-2 ring-[#C6A87C]' : ''}`}>
              <div className="flex items-start gap-3">
                <input 
                  type="checkbox" 
                  checked={isSelected}
                  onChange={() => toggleSelect(booking.id)}
                  className="w-4 h-4 mt-1 rounded border-gray-300 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <p className="font-medium text-[#1A1A1A]">{booking.guest_name}</p>
                      <p className="text-xs text-[#5A5A5A]">{booking.guest_phone}</p>
                    </div>
                    {getStatusBadge(booking.payment_status)}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div>
                      <span className="text-[#5A5A5A]">Cabañas:</span>
                      <p className="font-medium">{cabinNames.length > 1 ? `${cabinNames.length} cabañas` : cabinNames[0]}</p>
                    </div>
                    <div>
                      <span className="text-[#5A5A5A]">Huéspedes:</span>
                      <p className="font-medium">{booking.guests} personas</p>
                    </div>
                    <div>
                      <span className="text-[#5A5A5A]">Fechas:</span>
                      <p className="font-medium text-xs">{booking.check_in} → {booking.check_out}</p>
                    </div>
                    <div>
                      <span className="text-[#5A5A5A]">Total:</span>
                      <p className="font-bold text-[#2F3E30]">${booking.total_price.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-[#E5E0D6]">
                    {booking.payment_status === 'pending' && (
                      <button onClick={() => updateBookingStatus(booking.id, 'paid')} className="flex-1 py-2 text-sm text-green-600 bg-green-50 rounded flex items-center justify-center gap-1">
                        <CheckCircle className="w-4 h-4" /> Pagado
                      </button>
                    )}
                    {booking.payment_status !== 'cancelled' && (
                      <button onClick={() => updateBookingStatus(booking.id, 'cancelled')} className="flex-1 py-2 text-sm text-red-600 bg-red-50 rounded flex items-center justify-center gap-1">
                        <XCircle className="w-4 h-4" /> Cancelar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {displayedBookings.length === 0 && (
          <div className="bg-white p-12 rounded-sm card-shadow text-center">
            <Calendar className="w-12 h-12 text-[#C6A87C] mx-auto mb-4 opacity-50" />
            <p className="text-[#5A5A5A]">No hay reservaciones{hasFilters ? ' con estos filtros' : ''}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Pricing Manager - Combined Base Prices + Seasons
const PricingManager = ({ cabins, seasons, toggleSeason, deleteSeason, fetchData, getAuthHeaders }) => {
  const [editingPrices, setEditingPrices] = useState({});
  const [savingPrice, setSavingPrice] = useState(null);
  const [isAddSeasonDialogOpen, setIsAddSeasonDialogOpen] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [seasonFormData, setSeasonFormData] = useState({ name: '', start_date: '', end_date: '', price_multiplier: 1.3 });

  const handlePriceChange = (cabinId, value) => {
    setEditingPrices({ ...editingPrices, [cabinId]: value });
  };

  const savePrice = async (cabinId) => {
    const newPrice = editingPrices[cabinId];
    if (!newPrice || newPrice <= 0) {
      toast.error('El precio debe ser mayor a 0');
      return;
    }

    setSavingPrice(cabinId);
    try {
      await axios.put(`${API}/admin/cabins/${cabinId}/price`, { price_per_night: parseFloat(newPrice) }, getAuthHeaders());
      toast.success('Precio actualizado');
      setEditingPrices({ ...editingPrices, [cabinId]: undefined });
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar precio');
    } finally {
      setSavingPrice(null);
    }
  };

  const handleSaveSeason = async () => {
    try {
      if (editingSeason) {
        await axios.patch(`${API}/admin/seasons/${editingSeason.id}`, seasonFormData, getAuthHeaders());
      } else {
        await axios.post(`${API}/admin/seasons`, seasonFormData, getAuthHeaders());
      }
      toast.success(editingSeason ? 'Temporada actualizada' : 'Temporada creada');
      setIsAddSeasonDialogOpen(false);
      setEditingSeason(null);
      setSeasonFormData({ name: '', start_date: '', end_date: '', price_multiplier: 1.3 });
      fetchData();
    } catch (error) { toast.error('Error al guardar'); }
  };

  const openEditSeasonDialog = (season) => {
    setEditingSeason(season);
    setSeasonFormData({ name: season.name, start_date: season.start_date, end_date: season.end_date, price_multiplier: season.price_multiplier });
    setIsAddSeasonDialogOpen(true);
  };

  return (
    <div className="space-y-8">
      {/* Base Prices Section */}
      <div>
        <div className="mb-6">
          <h2 className="font-heading text-2xl font-semibold text-[#1A1A1A]">Precios Base por Cabaña</h2>
          <p className="text-[#5A5A5A] text-sm mt-1">Define el precio por noche de cada cabaña (antes de aplicar temporadas)</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cabins && cabins.map((cabin) => (
            <div key={cabin.id} className="bg-white p-6 rounded-sm card-shadow">
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 rounded-sm overflow-hidden flex-shrink-0">
                  <img src={cabin.images?.[0]} alt={cabin.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[#1A1A1A]">{cabin.name}</h3>
                  <p className="text-xs text-[#5A5A5A] mb-3">{cabin.capacity} huéspedes • {cabin.bedrooms} habitaciones</p>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-[#5A5A5A]">$</span>
                    <Input
                      type="number"
                      value={editingPrices[cabin.id] !== undefined ? editingPrices[cabin.id] : cabin.price_per_night}
                      onChange={(e) => handlePriceChange(cabin.id, e.target.value)}
                      className="w-32"
                    />
                    <span className="text-[#5A5A5A] text-sm">MXN/noche</span>
                    
                    {editingPrices[cabin.id] !== undefined && editingPrices[cabin.id] !== cabin.price_per_night.toString() && (
                      <button
                        onClick={() => savePrice(cabin.id)}
                        disabled={savingPrice === cabin.id}
                        className="px-3 py-1 bg-[#2F3E30] text-white text-sm rounded-sm hover:bg-[#1F2B21] disabled:opacity-50"
                      >
                        {savingPrice === cabin.id ? '...' : 'Guardar'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Divider */}
      <hr className="border-[#E5E0D6]" />

      {/* Seasons Section */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-[#1A1A1A]">Tarifas por Temporada</h2>
            <p className="text-[#5A5A5A] text-sm mt-1">Define incrementos de precio para diferentes épocas del año</p>
          </div>
          <Dialog open={isAddSeasonDialogOpen} onOpenChange={(open) => { setIsAddSeasonDialogOpen(open); if (!open) { setEditingSeason(null); setSeasonFormData({ name: '', start_date: '', end_date: '', price_multiplier: 1.3 }); } }}>
            <DialogTrigger asChild><button className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" /> Nueva Temporada</button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editingSeason ? 'Editar Temporada' : 'Nueva Temporada'}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                <div><Label>Nombre de la temporada</Label><Input value={seasonFormData.name} onChange={(e) => setSeasonFormData({...seasonFormData, name: e.target.value})} placeholder="Ej: Semana Santa" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Fecha inicio (MM-DD)</Label><Input value={seasonFormData.start_date} onChange={(e) => setSeasonFormData({...seasonFormData, start_date: e.target.value})} placeholder="04-01" /></div>
                  <div><Label>Fecha fin (MM-DD)</Label><Input value={seasonFormData.end_date} onChange={(e) => setSeasonFormData({...seasonFormData, end_date: e.target.value})} placeholder="04-20" /></div>
                </div>
                <div>
                  <Label>Incremento de precio</Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" step="0.1" min="1" max="3" value={seasonFormData.price_multiplier} onChange={(e) => setSeasonFormData({...seasonFormData, price_multiplier: parseFloat(e.target.value)})} />
                    <span className="text-[#5A5A5A] whitespace-nowrap font-medium">= +{Math.round((seasonFormData.price_multiplier - 1) * 100)}%</span>
                  </div>
                </div>
                <p className="text-xs text-[#5A5A5A]">Usa 00-00 en fechas para aplicar solo a fines de semana</p>
              </div>
              <DialogFooter><button onClick={() => setIsAddSeasonDialogOpen(false)} className="btn-secondary">Cancelar</button><button onClick={handleSaveSeason} className="btn-primary">Guardar</button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-4">
          {seasons && seasons.map((season) => (
            <div key={season.id} className={`bg-white p-5 rounded-sm card-shadow ${!season.is_active ? 'opacity-60' : ''}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${season.is_active ? 'bg-[#C6A87C]/20' : 'bg-gray-100'}`}>
                    <Percent className={`w-5 h-5 ${season.is_active ? 'text-[#C6A87C]' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A]">{season.name}</h3>
                    <p className="text-sm text-[#5A5A5A]">{season.start_date === '00-00' ? 'Todos los fines de semana' : `${season.start_date} al ${season.end_date}`}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <span className="text-xl font-heading font-bold text-[#2F3E30]">+{Math.round((season.price_multiplier - 1) * 100)}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={season.is_active} onCheckedChange={() => toggleSeason(season.id)} />
                    <button onClick={() => openEditSeasonDialog(season)} className="p-2 text-blue-600 hover:bg-blue-50 rounded"><Edit className="w-4 h-4" /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>¿Eliminar temporada?</AlertDialogTitle></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => deleteSeason(season.id)}>Eliminar</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {(!seasons || seasons.length === 0) && (
            <div className="bg-white p-8 rounded-sm card-shadow text-center">
              <p className="text-[#5A5A5A]">No hay temporadas configuradas</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Seasons Manager - Keep for backwards compatibility but redirect to PricingManager
const SeasonsManager = PricingManager;

// Photos Manager - FIXED
const PhotosManager = ({ cabins, fetchData, getAuthHeaders }) => {
  const [newImageUrl, setNewImageUrl] = useState('');
  const [selectedCabinId, setSelectedCabinId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Helper to resolve image URLs (handles relative paths from uploads)
  const getImageUrl = (image) => {
    if (!image) return '';
    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${backendUrl}${image}`;
  };

  const addImage = async () => {
    if (!newImageUrl || !selectedCabinId) return;
    setUploading(true);
    try {
      await axios.post(`${API}/admin/cabins/${selectedCabinId}/images/add?image_url=${encodeURIComponent(newImageUrl)}`, {}, getAuthHeaders());
      toast.success('Imagen agregada');
      setNewImageUrl('');
      setIsDialogOpen(false);
      setSelectedCabinId(null);
      fetchData();
    } catch (error) {
      toast.error('Error al agregar imagen');
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (cabinId, imageUrl) => {
    try {
      await axios.delete(`${API}/admin/cabins/${cabinId}/images?image_url=${encodeURIComponent(imageUrl)}`, getAuthHeaders());
      toast.success('Imagen eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar imagen');
    }
  };

  const openAddDialog = (cabinId) => {
    setSelectedCabinId(cabinId);
    setNewImageUrl('');
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-[#1A1A1A]">Gestión de Fotos</h2>
        <p className="text-[#5A5A5A] text-sm mt-1">Agrega o elimina fotos de cada cabaña</p>
      </div>

      {cabins && cabins.length > 0 ? (
        <div className="grid gap-6">
          {cabins.map((cabin) => (
            <div key={cabin.id} className="bg-white p-6 rounded-sm card-shadow">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-xl font-semibold text-[#1A1A1A]">{cabin.name}</h3>
                  <p className="text-sm text-[#5A5A5A]">{cabin.images?.length || 0} fotos</p>
                </div>
                <button
                  onClick={() => openAddDialog(cabin.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#2F3E30] text-white rounded-sm hover:bg-[#1F2B21] text-sm"
                >
                  <Plus className="w-4 h-4" /> Agregar foto
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {cabin.images && cabin.images.map((image, idx) => (
                  <div key={idx} className="relative group aspect-square rounded-sm overflow-hidden bg-gray-100">
                    <img src={getImageUrl(image)} alt={`${cabin.name} ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = '0.5'; }} />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button
                        onClick={() => deleteImage(cabin.id, image)}
                        className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {(!cabin.images || cabin.images.length === 0) && (
                  <div className="col-span-full text-center py-8 text-[#5A5A5A]">
                    No hay fotos. Haz clic en "Agregar foto" para subir imágenes.
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white p-12 rounded-sm card-shadow text-center">
          <p className="text-[#5A5A5A]">Cargando cabañas...</p>
        </div>
      )}

      {/* Add Image Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar foto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>URL de la imagen</Label>
              <Input
                value={newImageUrl}
                onChange={(e) => setNewImageUrl(e.target.value)}
                placeholder="https://ejemplo.com/imagen.jpg"
              />
            </div>
            {newImageUrl && (
              <div className="aspect-video rounded-sm overflow-hidden bg-gray-100">
                <img
                  src={newImageUrl}
                  alt="Preview"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            <div className="bg-[#F5F2EB] p-3 rounded-sm text-sm">
              <p className="font-medium text-[#1A1A1A] mb-2">¿Cómo subir fotos?</p>
              <ol className="list-decimal list-inside space-y-1 text-[#5A5A5A]">
                <li>Sube tu foto a <a href="https://imgur.com/upload" target="_blank" rel="noopener noreferrer" className="text-[#2F3E30] underline">Imgur</a></li>
                <li>Haz clic derecho en la imagen → "Copiar dirección de imagen"</li>
                <li>Pega la URL aquí</li>
              </ol>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setIsDialogOpen(false)} className="btn-secondary">Cancelar</button>
            <button onClick={addImage} disabled={!newImageUrl || uploading} className="btn-primary disabled:opacity-50">
              {uploading ? 'Agregando...' : 'Agregar'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Reviews Manager
const ReviewsManager = ({ reviews, formatDate, toggleReviewApproval, deleteReview }) => (
  <div className="grid gap-4">
    {reviews.map((review) => (
      <div key={review.id} className="bg-white p-6 rounded-sm card-shadow">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h4 className="font-medium text-[#1A1A1A]">{review.guest_name}</h4>
              <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-[#C6A87C] text-[#C6A87C]' : 'text-gray-300'}`} />)}</div>
              {!review.is_approved && <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">Pendiente</span>}
            </div>
            <p className="text-[#5A5A5A] text-sm mb-2">{review.comment}</p>
            <p className="text-xs text-[#5A5A5A]">{formatDate(review.created_at)}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => toggleReviewApproval(review.id, review.is_approved)} className={`p-2 rounded ${review.is_approved ? 'text-yellow-600 hover:bg-yellow-50' : 'text-green-600 hover:bg-green-50'}`}>
              {review.is_approved ? <Eye className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild><button className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
              <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>¿Eliminar reseña?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => deleteReview(review.id)}>Eliminar</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    ))}
    {reviews.length === 0 && <div className="bg-white p-12 rounded-sm card-shadow text-center"><p className="text-[#5A5A5A]">No hay reseñas</p></div>}
  </div>
);

// Messages Manager
const MessagesManager = ({ messages, formatDate, deleteMessage, getAuthHeaders }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === messages.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(messages.map(m => m.id));
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.length === 0) return;
    setDeleting(true);
    try {
      await Promise.all(selectedIds.map(id => deleteMessage(id)));
      setSelectedIds([]);
      toast.success(`${selectedIds.length} mensaje(s) eliminado(s)`);
    } catch (error) {
      toast.error('Error al eliminar mensajes');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Selection controls */}
      {messages.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-sm card-shadow">
          <label className="flex items-center gap-2 cursor-pointer">
            <input 
              type="checkbox" 
              checked={selectedIds.length === messages.length && messages.length > 0}
              onChange={toggleSelectAll}
              className="w-4 h-4 rounded border-gray-300"
            />
            <span className="text-sm text-[#5A5A5A]">Seleccionar todos</span>
          </label>
          {selectedIds.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="ml-auto px-3 py-1.5 bg-red-500 text-white text-sm rounded-sm hover:bg-red-600 flex items-center gap-2">
                  <Trash2 className="w-4 h-4" />
                  Eliminar ({selectedIds.length})
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar {selectedIds.length} mensaje(s)?</AlertDialogTitle>
                  <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteSelected} disabled={deleting}>
                    {deleting ? 'Eliminando...' : 'Eliminar'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      )}

      {/* Messages list */}
      <div className="grid gap-4">
        {messages.map((message) => (
          <div key={message.id} className={`bg-white p-4 sm:p-6 rounded-sm card-shadow ${selectedIds.includes(message.id) ? 'ring-2 ring-[#C6A87C]' : ''}`}>
            <div className="flex items-start gap-3">
              <input 
                type="checkbox" 
                checked={selectedIds.includes(message.id)}
                onChange={() => toggleSelect(message.id)}
                className="w-4 h-4 mt-1 rounded border-gray-300 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mb-2">
                  <h4 className="font-medium text-[#1A1A1A] truncate">{message.name}</h4>
                  <a 
                    href={`https://wa.me/${message.phone.replace(/\D/g, '')}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-sm text-green-600 hover:underline flex items-center gap-1"
                  >
                    <MessageSquare className="w-3 h-3" />
                    {message.phone}
                  </a>
                </div>
                <p className="text-[#5A5A5A] text-sm mb-2 break-words">{message.message}</p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <p className="text-xs text-[#5A5A5A]">{formatDate(message.created_at)}</p>
                  {message.email && <p className="text-xs text-[#5A5A5A] truncate">{message.email}</p>}
                </div>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button className="p-2 text-red-600 hover:bg-red-50 rounded flex-shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>¿Eliminar mensaje?</AlertDialogTitle></AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={() => deleteMessage(message.id)}>Eliminar</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
        {messages.length === 0 && (
          <div className="bg-white p-12 rounded-sm card-shadow text-center">
            <MessageSquare className="w-12 h-12 text-[#C6A87C] mx-auto mb-4 opacity-50" />
            <p className="text-[#5A5A5A]">No hay mensajes</p>
          </div>
        )}
      </div>
    </div>
  );
};

// FAQs Manager
const FAQsManager = ({ faqs, fetchData, getAuthHeaders }) => {
  const [editingFaq, setEditingFaq] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    question_es: '',
    question_en: '',
    answer_es: '',
    answer_en: '',
    order: 0
  });
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setFormData({ question_es: '', question_en: '', answer_es: '', answer_en: '', order: 0 });
    setEditingFaq(null);
    setIsCreating(false);
  };

  const handleEdit = (faq) => {
    setFormData({
      question_es: faq.question_es,
      question_en: faq.question_en,
      answer_es: faq.answer_es,
      answer_en: faq.answer_en,
      order: faq.order
    });
    setEditingFaq(faq);
    setIsCreating(false);
  };

  const handleCreate = () => {
    resetForm();
    setFormData(prev => ({ ...prev, order: faqs.length + 1 }));
    setIsCreating(true);
  };

  const handleSave = async () => {
    if (!formData.question_es || !formData.answer_es) {
      toast.error('La pregunta y respuesta en español son obligatorias');
      return;
    }
    setSaving(true);
    try {
      if (editingFaq) {
        await axios.put(`${API}/admin/faqs/${editingFaq.id}`, formData, getAuthHeaders());
        toast.success('FAQ actualizada');
      } else {
        await axios.post(`${API}/admin/faqs`, formData, getAuthHeaders());
        toast.success('FAQ creada');
      }
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Error al guardar FAQ');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (faqId) => {
    try {
      await axios.patch(`${API}/admin/faqs/${faqId}/toggle`, {}, getAuthHeaders());
      toast.success('FAQ actualizada');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDelete = async (faqId) => {
    try {
      await axios.delete(`${API}/admin/faqs/${faqId}`, getAuthHeaders());
      toast.success('FAQ eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-heading text-2xl font-semibold text-[#1A1A1A]">Preguntas Frecuentes</h2>
          <p className="text-[#5A5A5A] text-sm mt-1">Gestiona las FAQs que aparecen en la página de contacto</p>
        </div>
        <button onClick={handleCreate} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> Nueva FAQ
        </button>
      </div>

      {/* Form for creating/editing */}
      {(isCreating || editingFaq) && (
        <div className="bg-white p-6 rounded-sm card-shadow">
          <h3 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-4">
            {editingFaq ? 'Editar FAQ' : 'Nueva FAQ'}
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-medium text-[#2F3E30] flex items-center gap-2">
                <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs">ES</span>
                Español
              </h4>
              <div>
                <Label>Pregunta *</Label>
                <Input
                  value={formData.question_es}
                  onChange={(e) => setFormData({ ...formData, question_es: e.target.value })}
                  placeholder="¿Cuál es tu pregunta?"
                />
              </div>
              <div>
                <Label>Respuesta *</Label>
                <Textarea
                  value={formData.answer_es}
                  onChange={(e) => setFormData({ ...formData, answer_es: e.target.value })}
                  placeholder="Escribe la respuesta..."
                  rows={4}
                />
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="font-medium text-[#2F3E30] flex items-center gap-2">
                <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs">EN</span>
                English
              </h4>
              <div>
                <Label>Question</Label>
                <Input
                  value={formData.question_en}
                  onChange={(e) => setFormData({ ...formData, question_en: e.target.value })}
                  placeholder="What is your question?"
                />
              </div>
              <div>
                <Label>Answer</Label>
                <Textarea
                  value={formData.answer_en}
                  onChange={(e) => setFormData({ ...formData, answer_en: e.target.value })}
                  placeholder="Write the answer..."
                  rows={4}
                />
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label>Orden:</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                className="w-20"
                min={0}
              />
            </div>
            <div className="flex-1" />
            <button onClick={resetForm} className="px-4 py-2 text-[#5A5A5A] hover:bg-gray-100 rounded-sm">
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2 disabled:opacity-50">
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Guardar
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* FAQs List */}
      <div className="space-y-4">
        {faqs.map((faq) => (
          <div key={faq.id} className={`bg-white p-6 rounded-sm card-shadow ${!faq.is_active ? 'opacity-60' : ''}`}>
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-[#F5F2EB] px-2 py-1 rounded">#{faq.order}</span>
                  {!faq.is_active && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">Inactiva</span>}
                </div>
                <h4 className="font-semibold text-[#1A1A1A] mb-1">{faq.question_es}</h4>
                <p className="text-[#5A5A5A] text-sm mb-3">{faq.answer_es}</p>
                {faq.question_en && (
                  <div className="border-t pt-3 mt-3">
                    <p className="text-sm text-blue-700 font-medium">{faq.question_en}</p>
                    <p className="text-sm text-[#5A5A5A]">{faq.answer_en}</p>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleToggle(faq.id)} className={`p-2 rounded ${faq.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`} title={faq.is_active ? 'Desactivar' : 'Activar'}>
                  {faq.is_active ? <Eye className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                </button>
                <button onClick={() => handleEdit(faq)} className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                  <Edit className="w-4 h-4" />
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>¿Eliminar FAQ?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(faq.id)}>Eliminar</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        ))}
        {faqs.length === 0 && (
          <div className="bg-white p-12 rounded-sm card-shadow text-center">
            <HelpCircle className="w-12 h-12 text-[#C6A87C] mx-auto mb-4" />
            <p className="text-[#5A5A5A]">No hay preguntas frecuentes</p>
            <button onClick={handleCreate} className="mt-4 btn-secondary">Crear primera FAQ</button>
          </div>
        )}
      </div>
    </div>
  );
};

// Settings Manager - NEW
const SettingsManager = ({ businessInfo, fetchData, getAuthHeaders }) => {
  const { logout } = useAdmin();
  const [formData, setFormData] = useState({
    whatsapp: '',
    email: '',
    address: '',
    check_in_time: '15:00',
    check_out_time: '12:00',
    bank_name: '',
    bank_account_holder: '',
    bank_clabe: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (businessInfo) {
      setFormData({
        whatsapp: businessInfo.whatsapp || '',
        email: businessInfo.email || '',
        address: businessInfo.address || '',
        check_in_time: businessInfo.check_in_time || '15:00',
        check_out_time: businessInfo.check_out_time || '12:00',
        bank_name: businessInfo.bank_name || '',
        bank_account_holder: businessInfo.bank_account_holder || '',
        bank_clabe: businessInfo.bank_clabe || ''
      });
    }
  }, [businessInfo]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/business-info`, formData, getAuthHeaders());
      toast.success('Configuración guardada');
      fetchData();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, getAuthHeaders());
      
      toast.success('Contraseña actualizada. Debes iniciar sesión de nuevo.');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      
      // Logout after password change
      setTimeout(() => {
        logout();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-[#1A1A1A]">Configuración</h2>
        <p className="text-[#5A5A5A] text-sm mt-1">Modifica los datos de contacto y configuración de tu negocio</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contact Info */}
        <div className="bg-white p-6 rounded-sm card-shadow">
          <h3 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
            <Phone className="w-5 h-5 text-[#2F3E30]" /> Datos de Contacto
          </h3>
          <div className="space-y-4">
            <div>
              <Label>WhatsApp</Label>
              <Input
                value={formData.whatsapp}
                onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                placeholder="+52 33 1234 5678"
              />
              <p className="text-xs text-[#5A5A5A] mt-1">Incluye el código de país</p>
            </div>
            <div>
              <Label>Email (opcional)</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="contacto@ejemplo.com"
              />
            </div>
            <div>
              <Label>Dirección</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="Mazamitla, Jalisco, México"
              />
            </div>
          </div>
        </div>

        {/* Check-in/out times */}
        <div className="bg-white p-6 rounded-sm card-shadow">
          <h3 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#2F3E30]" /> Horarios
          </h3>
          <div className="space-y-4">
            <div>
              <Label>Hora de Check-in</Label>
              <Input
                type="time"
                value={formData.check_in_time}
                onChange={(e) => setFormData({...formData, check_in_time: e.target.value})}
              />
            </div>
            <div>
              <Label>Hora de Check-out</Label>
              <Input
                type="time"
                value={formData.check_out_time}
                onChange={(e) => setFormData({...formData, check_out_time: e.target.value})}
              />
            </div>
          </div>
        </div>

        {/* Bank Info */}
        <div className="bg-white p-6 rounded-sm card-shadow">
          <h3 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#2F3E30]" /> Datos Bancarios
          </h3>
          <div className="space-y-4">
            <div>
              <Label>Banco</Label>
              <Input
                value={formData.bank_name}
                onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                placeholder="Ej: BBVA, Santander, etc."
              />
            </div>
            <div>
              <Label>Titular de la cuenta</Label>
              <Input
                value={formData.bank_account_holder}
                onChange={(e) => setFormData({...formData, bank_account_holder: e.target.value})}
                placeholder="Nombre del titular"
              />
            </div>
            <div>
              <Label>CLABE Interbancaria</Label>
              <Input
                value={formData.bank_clabe}
                onChange={(e) => setFormData({...formData, bank_clabe: e.target.value})}
                placeholder="18 dígitos"
                maxLength={18}
              />
            </div>
          </div>
        </div>

        {/* Password Change */}
        <div className="bg-white p-6 rounded-sm card-shadow">
          <h3 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#2F3E30]" /> Cambiar Contraseña
          </h3>
          <div className="space-y-4">
            <div>
              <Label>Contraseña actual</Label>
              <Input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                placeholder="••••••••"
              />
            </div>
            <div>
              <Label>Nueva contraseña</Label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                placeholder="Mínimo 6 caracteres"
              />
            </div>
            <div>
              <Label>Confirmar nueva contraseña</Label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                placeholder="Repite la nueva contraseña"
              />
            </div>
            <button
              onClick={handleChangePassword}
              disabled={changingPassword || !passwordData.current_password || !passwordData.new_password}
              className="w-full px-4 py-2 bg-[#5A5A5A] text-white rounded-sm hover:bg-[#1A1A1A] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
            >
              {changingPassword ? 'Cambiando...' : 'Cambiar Contraseña'}
            </button>
          </div>
          <p className="text-xs text-[#5A5A5A] mt-3">
            Al cambiar la contraseña, se cerrará tu sesión actual.
          </p>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Guardar Configuración
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Users Manager - Multi-user system with roles
const UsersManager = ({ getAuthHeaders }) => {
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    role: 'viewer'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchCurrentUser();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, getAuthHeaders());
      setUsers(res.data || []);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const res = await axios.get(`${API}/admin/users/me`, getAuthHeaders());
      setCurrentUser(res.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const resetForm = () => {
    setFormData({ username: '', password: '', name: '', role: 'viewer' });
    setEditingUser(null);
    setIsDialogOpen(false);
  };

  const handleCreate = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '', // Don't show password
      name: user.name,
      role: user.role
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (!editingUser && !formData.username) {
      toast.error('El nombre de usuario es obligatorio');
      return;
    }
    if (!editingUser && !formData.password) {
      toast.error('La contraseña es obligatoria');
      return;
    }
    if (!editingUser && formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        const updateData = { name: formData.name, role: formData.role };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await axios.put(`${API}/admin/users/${editingUser.id}`, updateData, getAuthHeaders());
        toast.success('Usuario actualizado');
      } else {
        await axios.post(`${API}/admin/users`, formData, getAuthHeaders());
        toast.success('Usuario creado');
      }
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`${API}/admin/users/${user.id}`, { is_active: !user.is_active }, getAuthHeaders());
      toast.success(user.is_active ? 'Usuario desactivado' : 'Usuario activado');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, getAuthHeaders());
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full font-medium">Admin (Completo)</span>;
    }
    return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">Viewer (Reservas)</span>;
  };

  // Check if current user is admin (can manage users)
  const canManageUsers = currentUser?.role === 'admin';

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="font-heading text-2xl font-semibold text-[#1A1A1A]">Gestión de Usuarios</h2>
          <p className="text-[#5A5A5A] text-sm mt-1">
            {canManageUsers 
              ? 'Administra quién tiene acceso al panel (máximo 5 usuarios)'
              : 'Solo los administradores pueden gestionar usuarios'}
          </p>
        </div>
        {canManageUsers && users.length < 5 && (
          <button onClick={handleCreate} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nuevo Usuario
          </button>
        )}
      </div>

      {/* Role explanation */}
      <div className="bg-[#F5F2EB] p-4 rounded-sm">
        <h3 className="font-semibold text-[#1A1A1A] mb-2">Tipos de usuario:</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="flex items-start gap-2">
            <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full font-medium whitespace-nowrap">Admin</span>
            <span className="text-[#5A5A5A]">Acceso completo: reservas, tarifas, configuración, usuarios</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium whitespace-nowrap">Viewer</span>
            <span className="text-[#5A5A5A]">Solo ver todo y gestionar reservaciones (aceptar/rechazar)</span>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="space-y-4">
        {users.map((user) => {
          const isCurrentUser = currentUser?.id === user.id;
          return (
            <div key={user.id} className={`bg-white p-4 sm:p-6 rounded-sm card-shadow ${!user.is_active ? 'opacity-60' : ''}`}>
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${user.role === 'admin' ? 'bg-purple-500' : 'bg-blue-500'}`}>
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-[#1A1A1A]">{user.name}</h4>
                      {isCurrentUser && <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">Tú</span>}
                      {!user.is_active && <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">Inactivo</span>}
                    </div>
                    <p className="text-sm text-[#5A5A5A]">@{user.username}</p>
                    <div className="mt-1">{getRoleBadge(user.role)}</div>
                  </div>
                </div>
                
                {canManageUsers && !isCurrentUser && (
                  <div className="flex items-center gap-2 self-end sm:self-center">
                    <button
                      onClick={() => handleToggleActive(user)}
                      className={`p-2 rounded ${user.is_active ? 'text-yellow-600 hover:bg-yellow-50' : 'text-green-600 hover:bg-green-50'}`}
                      title={user.is_active ? 'Desactivar' : 'Activar'}
                    >
                      {user.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleEdit(user)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="p-2 text-red-600 hover:bg-red-50 rounded" title="Eliminar">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Se eliminará el usuario "{user.name}" y perderá acceso al panel.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(user.id)}>Eliminar</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {users.length === 0 && (
          <div className="bg-white p-12 rounded-sm card-shadow text-center">
            <Users className="w-12 h-12 text-[#C6A87C] mx-auto mb-4" />
            <p className="text-[#5A5A5A]">No hay usuarios registrados</p>
          </div>
        )}

        {users.length >= 5 && (
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-sm text-center">
            <p className="text-yellow-800 text-sm">Has alcanzado el límite de 5 usuarios</p>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!editingUser && (
              <div>
                <Label>Nombre de usuario *</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/\s/g, '') })}
                  placeholder="ejemplo: maria, juan123"
                />
                <p className="text-xs text-[#5A5A5A] mt-1">Sin espacios, se convertirá a minúsculas</p>
              </div>
            )}
            <div>
              <Label>Nombre completo *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="María García"
              />
            </div>
            <div>
              <Label>{editingUser ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={editingUser ? '••••••••' : 'Mínimo 6 caracteres'}
              />
            </div>
            <div>
              <Label>Rol</Label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-3 py-2 border border-[#E5E0D6] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#C6A87C]"
              >
                <option value="viewer">Viewer - Solo ver y gestionar reservas</option>
                <option value="admin">Admin - Acceso completo</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <button onClick={resetForm} className="btn-secondary">Cancelar</button>
            <button onClick={handleSave} disabled={saving} className="btn-primary disabled:opacity-50">
              {saving ? 'Guardando...' : (editingUser ? 'Actualizar' : 'Crear Usuario')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Component
const AdminPage = () => {
  const { isAuthenticated, loading, login } = useAdmin();
  if (loading) return <div className="min-h-screen bg-[#F5F2EB] flex items-center justify-center"><div className="spinner" /></div>;
  if (!isAuthenticated) return <AdminLogin onLogin={login} />;
  return <AdminDashboard />;
};

export default AdminPage;
